/**
 * Weekday session calendar for synthetic series (ADR-0037).
 *
 * Live mode reads `market.daily_bar.bar_date`. Local mode has no ingest and
 * therefore no operational calendar (`DatasetMeta.asOf` is null). These dates
 * are generator facts — deterministic weekdays ending at a pinned origin —
 * not "today" and not the NYSE holiday calendar. Length equals `WN[window]`.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const MS_PER_DAY = 86_400_000;

/**
 * Inclusive end of the synthetic weekday walk. Tuesday, so a 1-bar series
 * does not have to skip a weekend. Changing this only moves displayed dates;
 * it is not a golden-digest input.
 */
export const SYNTHETIC_SESSION_END_ON = '2024-12-31';

function utcDay(on: string): Date {
  const match = ISO_DATE.exec(on);
  const year = match?.[1];
  const month = match?.[2];
  const day = match?.[3];
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`synthetic session origin is not YYYY-MM-DD: ${on}`);
  }
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

function isWeekend(day: Date): boolean {
  const weekday = day.getUTCDay();
  return weekday === 0 || weekday === 6;
}

function shiftDays(day: Date, delta: number): Date {
  return new Date(day.getTime() + delta * MS_PER_DAY);
}

/* Imperative walk stays inside this module, same as generate-series.ts. */
/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */

function prevWeekday(day: Date): Date {
  let cursor = shiftDays(day, -1);
  while (isWeekend(cursor)) cursor = shiftDays(cursor, -1);
  return cursor;
}

/**
 * `count` weekdays ending on `endOn` (or the Friday before, if `endOn` is a
 * weekend), ascending. Empty when count is not a positive finite integer.
 */
export function syntheticSessions(
  count: number,
  endOn: string = SYNTHETIC_SESSION_END_ON,
): readonly string[] {
  if (!Number.isInteger(count) || count <= 0) return [];
  const out: string[] = [];
  let cursor = utcDay(endOn);
  if (isWeekend(cursor)) cursor = prevWeekday(cursor);
  out.push(cursor.toISOString().slice(0, 10));
  while (out.length < count) {
    cursor = prevWeekday(cursor);
    out.push(cursor.toISOString().slice(0, 10));
  }
  return Object.freeze(out.reverse());
}

/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */
